import { useEffect, useMemo, useState } from "react";
import { useScreenClass } from "react-grid-system";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import styled from "styled-components";
import theme from "../styles/theme";
import Button from "./Button";
import FlexBox, { FlexGrow } from "./FlexBox";
import { ClickableIcon } from "./Icon";
import Input, { InputTextArea } from "./Input";
import { Icon } from "@mui/material";
import MinimalSelect from "./MinimalSelect";
import Modal from "./Modal";
import { TOption } from "../types/Options";
import axios from "axios";
import { SaveButton } from "./PillarOverviewModify";

const Header = styled.h4`
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 28px;
  letter-spacing: 0px;
  text-align: left;
`;
const CustomFlex = styled(FlexBox)`
  padding: 20px 0;
  align-items: center;
  border-bottom: 1px solid #3c406c;
`;
const CustomFlexPlain = styled(FlexBox)`
  padding: 10px 0;
  align-items: flex-start;
`;
const CustomGrowPlain = styled(FlexGrow)`
  min-width: 160px;
  flex-grow: 0;
`;
const Label = styled.h6`
  // font-size: 16px;
  font-style: normal;
  font-weight: normal;
  line-height: 24px;
  letter-spacing: 0px;
  text-align: left;
`;
const WarningBox = styled.div`
  background-color: #fef3c7;
  border: 1px solid #fbbf24;
  border-radius: 6px;
  padding: 12px 16px;
  margin-top: 12px;
  color: #92400e;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;
type ConfirmTopicModalProps = {
  isOpen?: boolean;
  data: any;
  competencyData: any;
  onSubmit: (formData: any, topicData: any) => void;
  setOpen: (x: boolean) => void;
};
interface SkillwInputs {
  skill_title: string;
  assessment_title: string;
  assessment_description: string;
  skill_description: string;
  complexity_level: string | number;
  assessment_type: string | number;
  engineering_slo: any;
  computing_slo: any;
}
interface IOptions {
  value: number | string;
  label: string;
}
const ConfirmTopicModal = ({
  isOpen,
  setOpen,
  onSubmit,
  data,
  competencyData
}: ConfirmTopicModalProps): JSX.Element => {
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<SkillwInputs>({
    mode: "onBlur",
    defaultValues: {
      skill_title: "",
      assessment_type: "",
      assessment_description: "",
      assessment_title: "",
      engineering_slo: [],
      computing_slo: [],
      complexity_level: "",
      skill_description: "",
    },
  });
  const [optionsSkill, setOptionSkill] = useState<IOptions[]>([]);
  const [optionsType, setOptionType] = useState<IOptions[]>([]);
  const [optionsEng, setOptionEng] = useState<IOptions[]>([]);
  const [optionsComp, setOptionComp] = useState<IOptions[]>([]);

  const onSubmitOverview: SubmitHandler<SkillwInputs> = (fdata: any) => {
    onSubmit(fdata, data);
  };
  useEffect(() => {
    fetchAssessmentTypes();
    fetchCognitiveSkill();
    fetchLearningOutcome();
  }, []);
  const fetchLearningOutcome = () => {
    axios
      .get("/learningoutcome")
      .then((res: any) => {
        if (res.data.learning_outcomes) {
          const optionFormatEng: any = [];
          res.data.learning_outcomes?.engineering_slo?.map((v: any) =>
            optionFormatEng.push({ label: v.title, value: v.id })
          );
          setOptionEng(optionFormatEng);

          const optionFormatCom: any = [];
          res.data.learning_outcomes?.computing_slo?.map((v: any) =>
            optionFormatCom.push({ label: v.title, value: v.id })
          );
          setOptionComp(optionFormatCom);
        }
      })
      .catch((err) => {
        console.log("err", err);
      });
  };
  const fetchAssessmentTypes = () => {
    axios
      .get("/assessmenttype")
      .then((res: any) => {
        const optionFormat: any = [];
        res.data.assessmen_types?.map((v: any) =>
          optionFormat.push({ label: v.title, value: v.id })
        );
        setOptionType(optionFormat);
      })
      .catch((err) => {
        console.log("err", err);
      });
  };
  const fetchCognitiveSkill = () => {
    axios
      .get("/cognitiveskill")
      .then((res: any) => {
        const optionFormat: any = [];
        res.data.cognitiveskills?.map((v: any) =>
          optionFormat.push({ label: v.title, value: v.id })
        );
        optionFormat.sort((a: any, b: any) => a.value - b.value);
        // console.log(optionFormat);
        setOptionSkill(optionFormat);
      })
      .catch((err) => {
        console.log("err", err);
      });
  };

  return (
    <>
      <Modal
        isOpen={isOpen || false}
        setOpen={setOpen}
        customStyle={{
          overlay: {
            zIndex: "999958",
          },
          content: {
            maxWidth: "900px",
            padding: "0 24px",
            color: "black",
            background: "#FFFFFF",
            border: "1px solid #E7EBEE",
            boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.07)",
            borderRadius: "8px",
            marginTop: 10
          },
        }}
      >
        <form onSubmit={handleSubmit(onSubmitOverview)}>
          <CustomFlex>
            <FlexGrow>
              <Header>Create Skill</Header>
            </FlexGrow>
            <ClickableIcon
              size={3.125}
              fixedWidth={false}
              color="black"
              name="close"
              onClick={() => setOpen(false)}
            />
          </CustomFlex>
          <WarningBox>
            <Icon fontSize="small" style={{ paddingRight: 0 }}>
              warning
            </Icon>
            {competencyData?.has_cards ?
              <span>
                When this version was adopted, competency [{competencyData?.competency_code}] had [{competencyData?.original_skill_count}] assessed skills. You can add or delete skills as long as you do not change the scope of the competency by more than 50%. <br/><br/>
                There are students currently registered for this competency. Changes to skills may affect assessments and student progress. Please ensure your Canvas course reflects and aligns with the updated skills
              </span> :
              <span>
                When this version was adopted, competency [{competencyData?.competency_code}] had [{competencyData?.original_skill_count}] assessed skills. You can add or delete skills as long as you do not change the scope of the competency by more than 50%
              </span>
            }
          </WarningBox>

          <CustomFlex
            style={{
              position: "relative",
              borderBottom: "none",
              alignItems: "flex-start",
              overflowY: "scroll",
              overflowX: "hidden",
            }}
          >
            <FlexGrow>
              <CustomFlexPlain>
                <CustomGrowPlain>
                  <Label>Skill Title<span style={{ color: "red" }}> *</span></Label>
                </CustomGrowPlain>
                <FlexGrow>
                  <Input
                    required
                    style={{ width: "100%" }}
                    isFullWidth
                    {...register("skill_title", { required: true })}
                  />
                </FlexGrow>
              </CustomFlexPlain>

              <CustomFlexPlain>
                <CustomGrowPlain>
                  <Label>Description<span style={{ color: "red" }}> *</span></Label>
                </CustomGrowPlain>
                <FlexGrow>
                  <InputTextArea
                    required
                    maxLength={2048}
                    // required
                    style={{ width: "100%" }}
                    isFullWidth
                    {...register("skill_description")}
                  />
                </FlexGrow>
              </CustomFlexPlain>
              <CustomFlexPlain>
                <CustomGrowPlain>
                  <Label>Assessment Description</Label>
                </CustomGrowPlain>
                <FlexGrow>
                  <InputTextArea
                    required
                    maxLength={2048}
                    // required
                    style={{ width: "100%" }}
                    isFullWidth
                    {...register("assessment_description")}
                  />
                </FlexGrow>
              </CustomFlexPlain>

              <CustomFlexPlain>
                <CustomGrowPlain>
                  <Label>Assessment Type<span style={{ color: "red" }}> *</span></Label>
                </CustomGrowPlain>
                <FlexGrow>
                  <Controller
                    name="assessment_type"
                    control={control}
                    render={({ field }) => (
                      <MinimalSelect
                        required
                        {...field}
                        value={optionsType.find((c) => c.value === field.value)}
                        onChange={(item: TOption | unknown) => {
                          const val = item as TOption;
                          field.onChange(val.value as string);
                        }}
                        name={field.name}
                        options={optionsType}
                        styles={{
                          container: (base) => ({
                            ...base,
                            width: "100%",
                            maxWidth: "100%",
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999,
                            color: theme.color.lightBlueGray800,
                          }),
                        }}
                      />
                    )}
                  />
                </FlexGrow>
              </CustomFlexPlain>
              <CustomFlexPlain>
                <CustomGrowPlain>
                  <Label>Complexity Level<span style={{ color: "red" }}> *</span></Label>
                </CustomGrowPlain>
                <FlexGrow>
                  <Controller
                    name="complexity_level"
                    control={control}
                    render={({ field }) => (
                      <MinimalSelect
                        required
                        {...field}
                        value={optionsSkill?.find(
                          (c) => c.value === field.value
                        )}
                        onChange={(item: TOption | unknown) => {
                          const val = item as TOption;
                          field.onChange(val.value as string);
                        }}
                        name={field.name}
                        options={optionsSkill}
                        styles={{
                          container: (base) => ({
                            ...base,
                            width: "100%",
                            maxWidth: "100%",
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999,
                            color: theme.color.lightBlueGray800,
                          }),
                        }}
                      />
                    )}
                  />
                </FlexGrow>
              </CustomFlexPlain>
              <CustomFlexPlain>
                <CustomGrowPlain>
                  <Label>Engineering SLO</Label>
                </CustomGrowPlain>
                <FlexGrow>
                  <Controller
                    name="engineering_slo"
                    control={control}
                    render={({ field }) => (
                      <MinimalSelect
                        isClearable={false}
                        isMulti
                        {...field}
                        value={optionsEng?.find((c) => c.value === field.value)}
                        onChange={(item: TOption | unknown) => {
                          const val = item as TOption;
                          field.onChange(val);
                        }}
                        name={field.name}
                        options={optionsEng}
                        styles={{
                          container: (base) => ({
                            ...base,
                            width: "100%",
                            maxWidth: "100%",
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999,
                            color: theme.color.lightBlueGray800,
                          }),
                        }}
                      />
                    )}
                  />
                </FlexGrow>
              </CustomFlexPlain>
              <CustomFlexPlain>
                <CustomGrowPlain>
                  <Label>Computing SLO</Label>
                </CustomGrowPlain>
                <FlexGrow>
                  <Controller
                    name="computing_slo"
                    control={control}
                    render={({ field }) => (
                      <MinimalSelect
                        isClearable={false}
                        isMulti
                        {...field}
                        value={optionsEng?.find((c) => c.value === field.value)}
                        onChange={(item: TOption | unknown) => {
                          const val = item as TOption;
                          field.onChange(val);
                        }}
                        name={field.name}
                        options={optionsComp}
                        styles={{
                          container: (base) => ({
                            ...base,
                            width: "100%",
                            maxWidth: "100%",
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999,
                            color: theme.color.lightBlueGray800,
                          }),
                        }}
                      />
                    )}
                  />
                </FlexGrow>
              </CustomFlexPlain>
              <FlexBox>
                <FlexGrow></FlexGrow>
                <Button
                  className="reduce_opct_onhover"
                  onClick={() => setOpen(false)}
                  style={{
                    border: "2px solid #64748B",
                    padding: "8px 40px",
                    fontSize: "14px",
                  }}
                >
                  Cancel
                </Button>
                <SaveButton
                  className="reduce_opct_onhover"
                  type="submit"
                  style={{
                    // border: "2px solid #64748B",
                    padding: "8px 40px",
                    color: "white",
                  }}
                >
                  Create
                </SaveButton>
              </FlexBox>
            </FlexGrow>
          </CustomFlex>
        </form>
      </Modal>
    </>
  );
};

export default ConfirmTopicModal;
